/**
 * F5 — Desktop folder-drop event bus.
 *
 * Win95Desktop attaches onDragOver / onDrop to its root <div>. On a
 * folder drop, it dispatches a `repancy:portable-site-drop` event
 * carrying the ImportSource. NipePortableSiteDropDialog listens and
 * walks the source into a PortableSite, then prompts the user for
 * projection (Obsidian vault / SharePoint site / both) + schema profile.
 *
 * The event-based design keeps the dialog decoupled from Win95Desktop
 * — F5 can ship the surface without rewriting the desktop layout, and
 * other surfaces (the Import window's drop card) can dispatch the same
 * event to share the dialog.
 */

import type { ImportSource } from '../vault/importers/types';

export const PORTABLE_SITE_DROP_EVENT = 'repancy:portable-site-drop';

export interface PortableSiteDropDetail {
  source: ImportSource;
  /** Origin of the drop — used by the dialog for the surface label. */
  origin: 'desktop' | 'import-window';
}

export function dispatchPortableSiteDrop(detail: PortableSiteDropDetail): void {
  const event = new CustomEvent<PortableSiteDropDetail>(PORTABLE_SITE_DROP_EVENT, {
    detail,
  });
  window.dispatchEvent(event);
}

export function subscribePortableSiteDrop(
  handler: (detail: PortableSiteDropDetail) => void,
): () => void {
  const listener = (event: Event): void => {
    const ce = event as CustomEvent<PortableSiteDropDetail>;
    if (ce.detail) handler(ce.detail);
  };
  window.addEventListener(PORTABLE_SITE_DROP_EVENT, listener);
  return () => window.removeEventListener(PORTABLE_SITE_DROP_EVENT, listener);
}
